import React, { useEffect, useMemo, useRef } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import './PageTransition.css';

const TAB_ORDER = [
  '/',
  '/demander-logement',
  '/events',
  '/guide-adeef',
  '/temoignages',
  '/faq',
  '/contact'
];

function getTabIndex(pathname) {
  if (pathname === '/signup') return TAB_ORDER.indexOf('/demander-logement');
  return TAB_ORDER.indexOf(pathname);
}

export default function PageTransition({ location, children }) {
  const prevTabIndexRef = useRef(getTabIndex(location.pathname));
  const prevWasTabRef = useRef(prevTabIndexRef.current !== -1);
  const nodeRefsByKeyRef = useRef(new Map());

  const nextTabIndex = useMemo(() => getTabIndex(location.pathname), [location.pathname]);
  const nextIsTab = nextTabIndex !== -1;

  // Compute swipe decision synchronously so the transition class is correct on first navigation.
  const prevTabIndex = prevTabIndexRef.current;
  const prevWasTab = prevWasTabRef.current;

  const shouldSwipe = prevWasTab && nextIsTab && prevTabIndex !== -1 && nextTabIndex !== -1;

  const classNames = shouldSwipe ? 'pt-swipe' : 'pt-none';
  const direction = shouldSwipe ? (nextTabIndex > prevTabIndex ? 'left' : 'right') : 'left';

  // Update our previous refs after render so future navigations still have correct context.
  useEffect(() => {
    prevTabIndexRef.current = nextTabIndex;
    prevWasTabRef.current = nextIsTab;
  }, [nextIsTab, nextTabIndex]);

  const transitionKey = location.key ?? location.pathname;
  const nodeRef = useMemo(() => {
    const existing = nodeRefsByKeyRef.current.get(transitionKey);
    if (existing) return existing;
    const created = React.createRef();
    nodeRefsByKeyRef.current.set(transitionKey, created);
    return created;
  }, [transitionKey]);

  return (
    <div className="pt-viewport" data-dir={direction}>
      <TransitionGroup className="pt-stage">
        <CSSTransition
          key={transitionKey}
          nodeRef={nodeRef}
          classNames={classNames}
          timeout={classNames === 'pt-swipe' ? 320 : 0}
        >
          <div ref={nodeRef} className="pt-page">
            {children}
          </div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  );
}
